<template>
	<div v-if="isAuth" class="c-tag-manager-page">
		<PageHeader title="Tag manager" :show-menu="true">
			<template v-slot:title>Manage your tags</template>
			<template v-slot:menu>
				<button type="button" class="btn btn-primary" @click="openCreate">
					Add tag
				</button>
			</template>
		</PageHeader>

		<Constrain size="lg">
			<div class="c-tag-manager-page__content">
				<p>
					Manage the tags available in your workspace. Removing a tag also
					removes it from every saved ad that uses it.
				</p>
				<p v-if="loading">Loading tags…</p>
				<p v-else-if="loadError" role="alert">
					{{ loadError }}
					<button type="button" class="btn btn-box" @click="loadTags">
						Retry
					</button>
				</p>
				<div v-else>
					<div v-if="tags.length" class="c-tag-manager-page__search">
						<label class="form__label" for="tag-search">Search tags</label>
						<input
							id="tag-search"
							v-model="searchQuery"
							type="search"
							class="form-control"
							placeholder="Search tags"
						/>
					</div>
					<div class="c-tag-manager-page__list">
						<div v-if="!filteredTags.length" class="c-tag-manager-page__empty">
							<h3>{{ tags.length ? 'No matching tags' : 'No tags yet' }}</h3>
							<p v-if="!tags.length">
								Add a tag to make it available when saving ads.
							</p>
						</div>
						<div
							v-for="tag in visibleTags"
							:key="tag.name"
							class="c-tag-manager-page__row"
						>
							<div>
								<strong>{{ tag.name }}</strong>
								<span
									>{{ tag.adCount }}
									{{ tag.adCount === 1 ? 'ad' : 'ads' }}</span
								>
							</div>
							<button
								type="button"
								class="btn btn-box"
								:aria-label="`Remove ${tag.name}`"
								@click="openRemove(tag)"
							>
								Remove
							</button>
						</div>
						<div
							v-if="visibleCount < filteredTags.length"
							class="c-tag-manager-page__more"
						>
							<button
								type="button"
								class="btn btn-box"
								@click="visibleCount += 100"
							>
								Show more tags
							</button>
						</div>
					</div>
				</div>
			</div>
		</Constrain>

		<Modal v-model="createOpen" :closable="!processing">
			<template v-slot:title><h3>Add tag</h3></template>
			<form @submit.prevent="createTag">
				<div class="form-group">
					<label class="form__label" for="new-tag-name">Tag name</label>
					<input
						id="new-tag-name"
						v-model="newTagName"
						class="form-control"
						type="text"
						maxlength="22"
						required
						autocomplete="off"
					/>
				</div>
				<p v-if="formError" role="alert">{{ formError }}</p>
				<button
					type="submit"
					class="btn btn-primary"
					:disabled="processing || !newTagName.trim()"
				>
					{{ processing ? 'Adding…' : 'Add tag' }}
				</button>
			</form>
		</Modal>

		<Modal v-model="removeOpen" :closable="!processing">
			<template v-slot:title><h3>Remove tag?</h3></template>
			<div v-if="selectedTag">
				<p>
					Remove <strong>{{ selectedTag.name }}</strong> from your workspace and
					{{ selectedTag.adCount }}
					{{ selectedTag.adCount === 1 ? 'saved ad' : 'saved ads' }}?
				</p>
				<p v-if="formError" role="alert">{{ formError }}</p>
				<button
					type="button"
					class="btn btn-danger"
					:disabled="processing"
					@click="removeTag"
				>
					{{ processing ? 'Removing…' : 'Remove tag' }}
				</button>
			</div>
		</Modal>
	</div>
</template>

<script>
import Constrain from '@swipekit/components/utils/constrain.vue';
import PageHeader from '@swipekit/components/utils/page-header.vue';
import Modal from '@swipekit/components/modal/index.vue';

export default {
	name: 'TagManagerPage',
	components: { Constrain, PageHeader, Modal },
	data: function () {
		return {
			tags: [],
			loading: false,
			loadError: '',
			formError: '',
			processing: false,
			createOpen: false,
			removeOpen: false,
			newTagName: '',
			selectedTag: null,
			searchQuery: '',
			visibleCount: 100,
		};
	},
	computed: {
		isAuth: function () {
			return this.$store.getters['users/isAuth'];
		},
		filteredTags: function () {
			const query = this.searchQuery.trim().toLowerCase();
			if (!query) {
				return this.tags;
			}
			return this.tags.filter((tag) => tag.name.toLowerCase().includes(query));
		},
		visibleTags: function () {
			return this.filteredTags.slice(0, this.visibleCount);
		},
	},
	watch: {
		searchQuery: function () {
			this.visibleCount = 100;
		},
		isAuth: {
			immediate: true,
			handler: function (value) {
				if (value) {
					this.loadTags();
				}
			},
		},
	},
	methods: {
		async loadTags() {
			this.loading = true;
			this.loadError = '';
			try {
				this.tags = await this.$store.dispatch('workspaces/listTags');
			} catch (err) {
				this.loadError = 'Could not load tags. Please try again.';
			} finally {
				this.loading = false;
			}
		},
		openCreate: function () {
			this.newTagName = '';
			this.formError = '';
			this.createOpen = true;
		},
		openRemove: function (tag) {
			this.selectedTag = tag;
			this.formError = '';
			this.removeOpen = true;
		},
		readError: function (err, fallback) {
			const errors = err && err.response && err.response.data;
			return Array.isArray(errors) && errors[0] && errors[0].message
				? errors[0].message
				: fallback;
		},
		async createTag() {
			if (this.processing) {
				return;
			}
			this.processing = true;
			this.formError = '';
			try {
				this.tags = await this.$store.dispatch(
					'workspaces/createTag',
					this.newTagName.trim()
				);
				this.searchQuery = this.newTagName.trim();
				this.createOpen = false;
			} catch (err) {
				this.formError = this.readError(
					err,
					'Could not add tag. Please try again.'
				);
			} finally {
				this.processing = false;
			}
		},
		async removeTag() {
			if (this.processing || !this.selectedTag) {
				return;
			}
			this.processing = true;
			this.formError = '';
			try {
				this.tags = await this.$store.dispatch(
					'workspaces/removeTag',
					this.selectedTag.name
				);
				this.removeOpen = false;
				this.selectedTag = null;
			} catch (err) {
				this.formError = this.readError(
					err,
					'Could not remove tag. Please try again.'
				);
			} finally {
				this.processing = false;
			}
		},
	},
};
</script>

<style lang="scss">
.c-tag-manager-page {
	padding-bottom: var(--spacer);

	&__content {
		padding-top: var(--spacer-sm);
		> p {
			color: var(--color-font);
		}
	}

	&__list {
		margin-top: var(--spacer-sm);
		border: 1px solid var(--color-bg-5);
		border-radius: var(--border-radius);
		overflow: hidden;
	}

	&__search {
		max-width: 400px;
		margin-top: var(--spacer-sm);
	}

	&__more {
		padding: var(--margin-lg) var(--spacer-sm);
		background: var(--color-bg-1);
	}

	&__row {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: var(--spacer-sm);
		padding: var(--margin-lg) var(--spacer-sm);
		background: var(--color-bg-1);
		border-bottom: 1px solid var(--color-bg-5);
		font-size: var(--font-size-sm);

		&:last-child {
			border-bottom: 0;
		}
		strong {
			display: block;
			overflow-wrap: anywhere;
			font-weight: 500;
		}
		span {
			display: block;
			opacity: 0.75;
			font-size: var(--font-size-xs);
		}
		button {
			flex-shrink: 0;
		}
	}

	&__empty {
		padding: var(--spacer);
		background: var(--color-bg-1);
		p {
			margin-bottom: 0;
		}
	}

	.modal__title h3 {
		margin-bottom: 0;
	}
	.form-group {
		margin-bottom: var(--margin-lg);
	}
	.form-control {
		width: 100%;
	}
	[role='alert'] {
		color: var(--color-danger);
	}
}
</style>
